import { useEffect, useRef, useContext } from "react";
import { SRGBColorSpace, TextureLoader, RepeatWrapping, Box3, Vector3 } from "three";
import Constants from "../Constants";
import { useGLTF } from "@react-three/drei";
import MaterialInspectorContext from "../contextFiles/MaterialInspectorContext";

const ModelRenderer = (props) => {
    const { horizontalRepeat, verticalRepeat, setHorizontalRepeat, setVerticalRepeat, setLoaded } = useContext(MaterialInspectorContext);
    
    // Safety check for glb path
    if (!props.glb) {
        console.error("GLB path is undefined");
        return null;
    }
    
    const { scene, nodes, materials } = useGLTF(props.glb);
    const ref = useRef(null);
    const loadedTexturesRef = useRef(null);

    // Center the model
    useEffect(() => {
        if (scene && ref.current) {
            // Compute bounding box
            const box = new Box3().setFromObject(scene);
            const center = box.getCenter(new Vector3());
            const size = box.getSize(new Vector3());
            
            // Center the model
            scene.position.sub(center);
            
            // Scale the model to fit within a reasonable size (optional)
            const maxDimension = Math.max(size.x, size.y, size.z);
            if (maxDimension > 2) {
                const scale = 2 / maxDimension;
                scene.scale.setScalar(scale);
            }
            
            console.log('Model centered:', { center, size, maxDimension });
        }
    }, [scene]);

    const loadMaterial = async (texturesLoaded) => {
        let maps = props.materialInfoJson.maps;

        if (maps === undefined) {
            return;
        }

        let uRepeat = horizontalRepeat;
        let vRepeat = verticalRepeat;

        if (uRepeat == 0 && vRepeat == 0 && maps.length > 0) {
            setHorizontalRepeat(maps[0].u_repeat);
            setVerticalRepeat(maps[0].v_repeat);
        }

        // Get the material name based on the fabric type
        const materialName = props.fabricType === "Fabric" ? "Fabric" : "HardSurface";
        
        // Safety check for material existence
        if (!materials[materialName]) {
            console.error(`Material ${materialName} not found in GLB`);
            return;
        }

        // Store loaded textures for later use
        loadedTexturesRef.current = { textures: texturesLoaded, maps: maps, materialName };

        for (let index in maps) {
            let map = maps[index];
            materials[materialName].color = { isColor: true, r: 1, g: 1, b: 1 };

            if (map.type == "Base") {
                materials[materialName].map = texturesLoaded[index];
                materials[materialName].map.colorSpace = SRGBColorSpace;
                materials[materialName].map.flipY = false;
                materials[materialName].map.wrapS = RepeatWrapping;
                materials[materialName].map.wrapT = RepeatWrapping;
                materials[materialName].map.repeat.set(uRepeat, vRepeat);
            }
            if (map.type == "Roughness") {
                materials[materialName].roughnessMap = texturesLoaded[index];
                materials[materialName].roughness = 1;
                materials[materialName].roughnessMap.flipY = false;
                materials[materialName].roughnessMap.wrapS = RepeatWrapping;
                materials[materialName].roughnessMap.wrapT = RepeatWrapping;
                materials[materialName].roughnessMap.repeat.set(uRepeat, vRepeat);
            }
            if (map.type == "Normal") {
                materials[materialName].normalMap = texturesLoaded[index];
                materials[materialName].normalMap.flipY = false;
                materials[materialName].normalMap.wrapS = RepeatWrapping;
                materials[materialName].normalMap.wrapT = RepeatWrapping;
                materials[materialName].normalMap.repeat.set(uRepeat, vRepeat);
            }
            if (map.type == "Metalness") {
                materials[materialName].metalnessMap = texturesLoaded[index];
                materials[materialName].metalness = 1;
                materials[materialName].metalnessMap.flipY = false;
                materials[materialName].metalnessMap.wrapS = RepeatWrapping;
                materials[materialName].metalnessMap.wrapT = RepeatWrapping;
                materials[materialName].metalnessMap.repeat.set(uRepeat, vRepeat);
            }
        }

        materials[materialName].needsUpdate = true;
    };

    // Function to update texture repeat settings
    const updateTextureRepeats = () => {
        if (!loadedTexturesRef.current) return;
        
        const { textures: texturesLoaded, maps, materialName } = loadedTexturesRef.current;
        const material = materials[materialName];
        
        if (!material) return;

        for (let index in maps) {
            let map = maps[index];

            if (map.type == "Base" && material.map) {
                material.map.repeat.set(horizontalRepeat, verticalRepeat);
            }
            if (map.type == "Roughness" && material.roughnessMap) {
                material.roughnessMap.repeat.set(horizontalRepeat, verticalRepeat);
            }
            if (map.type == "Normal" && material.normalMap) {
                material.normalMap.repeat.set(horizontalRepeat, verticalRepeat);
            }
            if (map.type == "Metalness" && material.metalnessMap) {
                material.metalnessMap.repeat.set(horizontalRepeat, verticalRepeat);
            }
        }

        material.needsUpdate = true;
    };

    // Watch for changes in repeat values and update textures
    useEffect(() => {
        if (loadedTexturesRef.current) {
            updateTextureRepeats();
        }
    }, [horizontalRepeat, verticalRepeat]);

    useEffect(() => {
        const textureLoader = new TextureLoader();
        const maps = props.materialInfoJson.maps;

        // Safety check for maps
        if (!maps || maps.length === 0) {
            console.warn("No texture maps found");
            setLoaded(true);
            return;
        }

        // Load all textures asynchronously
        const texturePromises = maps.map((map) =>
            new Promise((resolve, reject) => {
                const url = `${Constants.BASE_URL_MATERIALS}${props.id}/PBRTextures/${map.url}`;
                textureLoader.load(
                    url,
                    (texture) => resolve(texture), // On load
                    undefined, // On progress (optional)
                    (error) => reject(error) // On error
                );
            })
        );

        Promise.all(texturePromises)
            .then(async (textures) => {
                await loadMaterial(textures);
                setLoaded(true);
            })
            .catch((error) => {
                console.error("Error loading textures:", error);
                setLoaded(true); // Still set loaded to prevent infinite loading state
            });

    }, [props]);

    return (
        <primitive ref={ref} object={scene} />
    );
};

export default ModelRenderer; 